// client/src/components/billingTransactions/TransactionList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingTransactionService from '../../services/billingTransactionService';

const TransactionList = () => {
  const { t } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Function to load billing transactions
  const loadTransactions = async () => {
    try {
      setLoading(true);
      const response = await billingTransactionService.getBillingTransactions();
      setTransactions(response.data);
      setError('');
    } catch (err) {
      setError(t('errorLoadingTransactions'));
    } finally {
      setLoading(false);
    }
  };

  // Load transactions on component mount
  useEffect(() => {
    loadTransactions();
  }, []);

  // Handle transaction deletion
  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await billingTransactionService.deleteBillingTransaction(transactionToDelete.id);
      setSuccess(t('transactionDeletedSuccess'));
      loadTransactions();
    } catch (err) {
      setError(t('errorDeletingTransaction'));
    } finally {
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTransactionToDelete(null);
  };

  // Handle details view
  const handleViewDetails = async (transaction) => {
    try {
      const response = await billingTransactionService.getBillingTransaction(transaction.id);
      setSelectedTransaction(response.data);
      setDetailsDialogOpen(true);
    } catch (err) {
      setError(t('errorLoadingTransactionDetails'));
    }
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedTransaction(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('billingTransactions')}
      </Typography>

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Transaction button - only for admins */}
      {isAdmin() && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to="/billing-transactions/create"
          >
            {t('addTransaction')}
          </Button>
        </Box>
      )}

      {/* Transactions Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noTransactionsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('transactionName')}</TableCell>
                <TableCell align="right">{t('totalValue')}</TableCell>
                <TableCell align="center">{t('distributionCount')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{transaction.name}</TableCell>
                  <TableCell align="right">{formatCurrency(transaction.total_value)}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={transaction.distributions?.length || 0} 
                      color="primary" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="info"
                      onClick={() => handleViewDetails(transaction)}
                    >
                      <ViewIcon />
                    </IconButton>
                    {isAdmin() && (
                      <>
                        <IconButton
                          color="primary"
                          component={RouterLink}
                          to={`/billing-transactions/edit/${transaction.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(transaction)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteTransactionConfirmation', { 
              name: transactionToDelete?.name 
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('transactionDetails')}</DialogTitle>
        <DialogContent>
          {selectedTransaction && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">{selectedTransaction.name}</Typography>
                <Typography variant="subtitle1">
                  {t('totalValue')}: {formatCurrency(selectedTransaction.total_value)}
                </Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom>{t('valueDistribution')}</Typography>
              
              {selectedTransaction.distributions?.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('accountName')}</TableCell>
                        <TableCell>{t('accountNumber')}</TableCell>
                        <TableCell align="right">{t('value')}</TableCell>
                        <TableCell align="right">{t('percentage')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTransaction.distributions.map((distribution) => (
                        <TableRow key={distribution.id}>
                          <TableCell>{distribution.bankAccount?.name}</TableCell>
                          <TableCell>{distribution.bankAccount?.account_number}</TableCell>
                          <TableCell align="right">{formatCurrency(distribution.value)}</TableCell>
                          <TableCell align="right">
                            {((distribution.value / selectedTransaction.total_value) * 100).toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2">{t('noDistributionsFound')}</Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TransactionList;